import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  INVITE_CODE_LENGTH,
  normalizeInviteCode,
  type LobbyListItem,
  type LobbyVisibility,
} from "@fortress/shared";
import {
  cancelMatch,
  createLobby,
  joinLobbyByCode,
  listLobbies,
  listMyMatches,
  resumeMatch,
} from "./auth/lobbies";
import { getActiveGameTokens } from "./net/activeGame";
import { getAppState } from "./appState";
import { MatchHistory } from "./MatchHistory";

/** One in-progress match of this account, as shown on the hub. `roomId` set →
 * a live room hosts it (RECONNECT = join it); null → it must be revived first
 * (RESUME = `POST /games/:id/resume`, then join). The guest-mode fallback
 * rows carry the locally stored reconnection token instead (no account to
 * query matches by) and no match info. */
interface HubMatch {
  /** Stable render key: gameId when known, else the roomId. */
  key: string;
  gameId: string | null;
  roomId: string | null;
  roundNumber: number | null;
  phase: string | null;
  players: string[];
  /** Guest (AUTH_DISABLED) fallback only — reconnect via stored token. */
  reconnectionToken?: string;
}

interface LobbyHubProps {
  onEnterRoom: (roomId: string, opts?: { spectate?: boolean }) => void;
  /** Guest fallback: re-enter a live game with the stored reconnection token. */
  onReconnect: (game: { roomId: string; reconnectionToken: string }) => void;
  /** The last reconnect attempt failed — the game is gone; shown once. */
  reconnectFailed?: boolean;
}

function statusLabel(status: LobbyListItem["status"]): string {
  switch (status) {
    case "waiting":
      return "Waiting";
    case "full":
      return "Full";
    case "in_progress":
      return "In progress";
  }
}

export function LobbyHub({
  onEnterRoom,
  onReconnect,
  reconnectFailed = false,
}: LobbyHubProps) {
  /** Hub tab: "play" is the existing create/join/browse surface; "matches"
   * is the account's finished-match history. */
  const [tab, setTab] = useState<"play" | "matches">("play");

  const [lobbies, setLobbies] = useState<LobbyListItem[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [listBusy, setListBusy] = useState(false);

  /** This account's in-progress matches. The games DB is the source of truth
   * (`GET /games/mine`); each row is either RECONNECT (a live room hosts the
   * match — plain `joinById`, the room's returning-participant gate seats the
   * account back, no local token needed, works cross-device) or RESUME (no
   * live room — revive from the save, then join). With AUTH_DISABLED there is
   * no account to query by, so the locally recorded rooms are offered as
   * token-based reconnects instead (a dead one fails on click). */
  const [myMatches, setMyMatches] = useState<HubMatch[]>([]);
  const refreshMyMatches = useCallback(async () => {
    try {
      const { authDisabled } = await getAppState();
      if (authDisabled) {
        const tokens = await getActiveGameTokens();
        setMyMatches(
          Object.entries(tokens).map(([roomId, reconnectionToken]) => ({
            key: roomId,
            gameId: null,
            roomId,
            roundNumber: null,
            phase: null,
            players: [],
            reconnectionToken,
          })),
        );
        return;
      }
      const matches = await listMyMatches();
      setMyMatches(
        matches.map((m) => ({
          key: m.gameId,
          gameId: m.gameId,
          roomId: m.roomId,
          roundNumber: m.roundNumber,
          phase: m.phase,
          players: m.players,
        })),
      );
    } catch {
      // Transient lookup failure — keep whatever was last shown.
    }
  }, []);

  /** RESUME in flight for this gameId (button busy), or null. */
  const [resumingId, setResumingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [matchError, setMatchError] = useState<string | null>(null);

  const onResume = useCallback(
    async (gameId: string) => {
      setResumingId(gameId);
      setMatchError(null);
      try {
        const { roomId } = await resumeMatch(gameId);
        onEnterRoom(roomId);
      } catch {
        setMatchError("Couldn't resume that game. Try again.");
        setResumingId(null);
        void refreshMyMatches();
      }
    },
    [onEnterRoom, refreshMyMatches],
  );

  const onCancelMatch = useCallback(async (gameId: string) => {
    setCancellingId(gameId);
    setMatchError(null);
    try {
      await cancelMatch(gameId);
      setMyMatches((matches) =>
        matches.filter((match) => match.gameId !== gameId),
      );
    } catch {
      setMatchError("Couldn't cancel that game. Try again.");
    } finally {
      setCancellingId(null);
    }
  }, []);

  const [visibility, setVisibility] = useState<LobbyVisibility>("public");
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [code, setCode] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setListBusy(true);
    setListError(null);
    try {
      setLobbies(await listLobbies());
    } catch (err) {
      setListError(err instanceof Error ? err.message : "list_failed");
    } finally {
      setListBusy(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    void refreshMyMatches();
    const id = window.setInterval(() => {
      void refresh();
      void refreshMyMatches();
    }, 5000);
    return () => window.clearInterval(id);
  }, [refresh, refreshMyMatches]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setCreateBusy(true);
    setCreateError(null);
    try {
      const created = await createLobby({ visibility, maxPlayers });
      onEnterRoom(created.roomId);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "create_failed");
      setCreateBusy(false);
    }
  }

  async function joinByCode(spectate: boolean) {
    const normalized = normalizeInviteCode(code);
    if (normalized.length !== INVITE_CODE_LENGTH) {
      setCodeError("Enter a 6-character code.");
      return;
    }
    setCodeBusy(true);
    setCodeError(null);
    try {
      const { roomId } = await joinLobbyByCode({
        code: normalized,
        ...(spectate ? { spectate: true } : {}),
      });
      onEnterRoom(roomId, spectate ? { spectate: true } : undefined);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "join_failed";
      setCodeError(
        msg === "not_found"
          ? "No lobby with that code."
          : msg === "not_joinable"
            ? "That lobby is full or already started."
            : "Couldn't join. Try again.",
      );
      setCodeBusy(false);
    }
  }

  async function onJoinCode(e: FormEvent) {
    e.preventDefault();
    await joinByCode(false);
  }

  async function onJoinListed(roomId: string) {
    setListError(null);
    onEnterRoom(roomId);
  }

  return (
    <div className="lobby-hub">
      <header className="lobby-hub-header">
        <h1 className="lobby-hub-title">Fortress</h1>
        <p className="lobby-hub-sub">Create a lobby, browse public games, or enter a code.</p>
      </header>

      <nav className="lobby-hub-tabs">
        <button
          type="button"
          className={`lobby-hub-tab${tab === "play" ? " is-active" : ""}`}
          onClick={() => setTab("play")}
        >
          Play
        </button>
        <button
          type="button"
          className={`lobby-hub-tab${tab === "matches" ? " is-active" : ""}`}
          onClick={() => setTab("matches")}
        >
          My matches
        </button>
      </nav>

      {tab === "matches" && <MatchHistory />}

      {tab === "play" && (
        <>
          {myMatches.map((match) => {
            const live = match.roomId !== null;
            const info = [
              match.phase === "setup"
                ? "Setting up"
                : match.roundNumber !== null
                  ? `Round ${match.roundNumber}`
                  : null,
              match.players.length > 0 ? match.players.join(", ") : null,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <section
                key={match.key}
                className="lobby-hub-panel lobby-hub-reconnect"
              >
                <div className="lobby-hub-reconnect-text">
                  <h2>Game in progress</h2>
                  <p>
                    {info || "Your seat is being held."}
                    {live ? " · Live — your seat is open." : " · Paused."}
                  </p>
                </div>
                {live ? (
                  <button
                    type="button"
                    className="auth-submit"
                    onClick={() =>
                      match.reconnectionToken
                        ? onReconnect({
                            roomId: match.roomId!,
                            reconnectionToken: match.reconnectionToken,
                          })
                        : onEnterRoom(match.roomId!)
                    }
                  >
                    Reconnect
                  </button>
                ) : (
                  <div className="lobby-hub-match-actions">
                    <button
                      type="button"
                      className="auth-submit"
                      disabled={resumingId !== null || cancellingId !== null}
                      onClick={() => match.gameId && void onResume(match.gameId)}
                    >
                      {resumingId === match.gameId ? "Resuming…" : "Resume play"}
                    </button>
                    <button
                      type="button"
                      className="lobby-hub-cancel-match"
                      aria-label="Cancel saved game"
                      title="Cancel saved game"
                      disabled={resumingId !== null || cancellingId !== null}
                      onClick={() =>
                        match.gameId && void onCancelMatch(match.gameId)
                      }
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M3 6h18M8 6V4h8v2m2 0-1 14H7L6 6m4 4v6m4-6v6" />
                      </svg>
                    </button>
                  </div>
                )}
              </section>
            );
          })}
          {matchError && (
            <p className="auth-error lobby-hub-reconnect-error">{matchError}</p>
          )}
          {reconnectFailed && myMatches.length === 0 && (
            <p className="auth-error lobby-hub-reconnect-error">
              Couldn&apos;t reconnect — that game is no longer available.
            </p>
          )}

          <div className="lobby-hub-grid">
            <section className="lobby-hub-panel">
              <h2>Create lobby</h2>
              <form className="lobby-hub-form" onSubmit={onCreate}>
                <label className="auth-label" htmlFor="lobby-max">
                  Max players
                </label>
                <select
                  id="lobby-max"
                  className="auth-input"
                  value={maxPlayers}
                  onChange={(e) => setMaxPlayers(Number(e.target.value))}
                  disabled={createBusy}
                >
                  {Array.from({ length: 7 }, (_, i) => i + 2).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>

                <fieldset className="lobby-hub-visibility">
                  <legend className="auth-label">Visibility</legend>
                  <label className="lobby-hub-radio">
                    <input
                      type="radio"
                      name="visibility"
                      checked={visibility === "public"}
                      onChange={() => setVisibility("public")}
                      disabled={createBusy}
                    />
                    Public
                  </label>
                  <label className="lobby-hub-radio">
                    <input
                      type="radio"
                      name="visibility"
                      checked={visibility === "private"}
                      onChange={() => setVisibility("private")}
                      disabled={createBusy}
                    />
                    Private
                  </label>
                </fieldset>

                <button className="auth-submit" type="submit" disabled={createBusy}>
                  {createBusy ? "Creating…" : "Create"}
                </button>
                {createError && <p className="auth-error">{createError}</p>}
              </form>
            </section>

            <section className="lobby-hub-panel">
              <h2>Join with code</h2>
              <form className="lobby-hub-form" onSubmit={onJoinCode}>
                <label className="auth-label" htmlFor="lobby-code">
                  Invite code
                </label>
                <input
                  id="lobby-code"
                  className="auth-input lobby-code-input"
                  type="text"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="ABC123"
                  maxLength={INVITE_CODE_LENGTH}
                  value={code}
                  onChange={(e) =>
                    setCode(
                      e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, "")
                        .slice(0, INVITE_CODE_LENGTH),
                    )
                  }
                  disabled={codeBusy}
                />
                <button className="auth-submit" type="submit" disabled={codeBusy}>
                  {codeBusy ? "Joining…" : "Join"}
                </button>
                <button
                  type="button"
                  className="lobby-hub-watch-code"
                  disabled={codeBusy}
                  title="Watch this game without playing"
                  onClick={() => void joinByCode(true)}
                >
                  Watch as spectator
                </button>
                {codeError && <p className="auth-error">{codeError}</p>}
              </form>
            </section>
          </div>

          <section className="lobby-hub-panel lobby-hub-list">
            <div className="lobby-hub-list-head">
              <h2>Public lobbies</h2>
              <button
                type="button"
                className="auth-link"
                onClick={() => void refresh()}
                disabled={listBusy}
              >
                Refresh
              </button>
            </div>
            {listError && <p className="auth-error">{listError}</p>}
            {lobbies.length === 0 && !listBusy && (
              <p className="lobby-hub-empty">No public lobbies right now.</p>
            )}
            <ul className="lobby-hub-rows">
              {lobbies.map((lobby) => {
                const joinable = lobby.status === "waiting";
                return (
                  <li key={lobby.roomId} className="lobby-hub-row">
                    <div className="lobby-hub-row-main">
                      <span className={`lobby-hub-status is-${lobby.status}`}>
                        {statusLabel(lobby.status)}
                      </span>
                      <span className="lobby-hub-counts">
                        {lobby.playerCount}/{lobby.maxPlayers}
                      </span>
                      {lobby.hostName && (
                        <span className="lobby-hub-host">{lobby.hostName}</span>
                      )}
                    </div>
                    <div className="lobby-hub-row-actions">
                      <button
                        type="button"
                        className="lobby-hub-join"
                        disabled={!joinable}
                        onClick={() => void onJoinListed(lobby.roomId)}
                      >
                        {joinable ? "Join" : "—"}
                      </button>
                      <button
                        type="button"
                        className="lobby-hub-watch"
                        title="Watch without playing"
                        onClick={() => onEnterRoom(lobby.roomId, { spectate: true })}
                      >
                        Watch
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
